/**
 * Year-end (bokslut) wizard constants — step definitions, Swedish copy
 * and readiness-check metadata.
 *
 * The step keys mirror the backend's year_end_processes columns
 * ({step}_completed / current_step) exactly — see YEAR_END_PLAN.md §4.3.
 * Only the steps returned in the overview payload's `steps` array are
 * rendered (an EF process has 5 of these 9).
 *
 * Copy rule (standing product principle): novice-first plain Swedish —
 * every step and every readiness check explains WHY it matters, no
 * accounting jargon without an inline explanation.
 */

import {
    ClipboardCheck,
    Scale,
    SlidersHorizontal,
    Calculator,
    FileText,
    PenLine,
    Send,
    FileSpreadsheet,
    Flag,
} from 'lucide-react';

export const StepEnum = {
    PREFLIGHT: 'preflight',
    RECONCILIATION: 'reconciliation',
    ADJUSTMENTS: 'adjustments',
    TAX: 'tax',
    ANNUAL_REPORT: 'annual_report',
    SIGNATURES_STAMMA: 'signatures_stamma',
    BOLAGSVERKET: 'bolagsverket',
    DEKLARATION: 'deklaration',
    POST_STAMMA: 'post_stamma',
};

/** Canonical AB order — the overview payload's `steps` array is the
 *  source of truth per process; this is only used for sorting/fallback. */
export const STEP_ORDER = [
    StepEnum.PREFLIGHT,
    StepEnum.RECONCILIATION,
    StepEnum.ADJUSTMENTS,
    StepEnum.TAX,
    StepEnum.ANNUAL_REPORT,
    StepEnum.SIGNATURES_STAMMA,
    StepEnum.BOLAGSVERKET,
    StepEnum.DEKLARATION,
    StepEnum.POST_STAMMA,
];

/**
 * Faserna — the four distinct activities the nine steps belong to. The
 * step chain is one guided path (the coupling is real: the ÅR derives
 * from the locked bokslut, undo cascades cross phases), but the phases
 * are what the user recognizes from the outside world: bokslutet locks
 * the ledger, årsredovisningen goes to Bolagsverket, deklarationen to
 * Skatteverket, and verkställigheten happens in the new year.
 */
export const PhaseEnum = {
    BOKSLUT: 'bokslut',
    ARSREDOVISNING: 'arsredovisning',
    DEKLARATION: 'deklaration',
    EFTER_STAMMAN: 'efter_stamman',
};

export const STEP_PHASES = {
    [StepEnum.PREFLIGHT]: PhaseEnum.BOKSLUT,
    [StepEnum.RECONCILIATION]: PhaseEnum.BOKSLUT,
    [StepEnum.ADJUSTMENTS]: PhaseEnum.BOKSLUT,
    [StepEnum.TAX]: PhaseEnum.BOKSLUT,
    [StepEnum.ANNUAL_REPORT]: PhaseEnum.ARSREDOVISNING,
    [StepEnum.SIGNATURES_STAMMA]: PhaseEnum.ARSREDOVISNING,
    [StepEnum.BOLAGSVERKET]: PhaseEnum.ARSREDOVISNING,
    [StepEnum.DEKLARATION]: PhaseEnum.DEKLARATION,
    [StepEnum.POST_STAMMA]: PhaseEnum.EFTER_STAMMAN,
};

export const PHASE_TITLES = {
    [PhaseEnum.BOKSLUT]: 'Bokslutet',
    [PhaseEnum.ARSREDOVISNING]: 'Årsredovisningen',
    [PhaseEnum.DEKLARATION]: 'Deklarationen',
    [PhaseEnum.EFTER_STAMMAN]: 'Efter stämman',
};

export const PHASE_DESCRIPTIONS = {
    [PhaseEnum.BOKSLUT]: 'Året summeras och låses i bokföringen',
    [PhaseEnum.ARSREDOVISNING]: 'Skrivs under, fastställs av stämman och lämnas till Bolagsverket',
    [PhaseEnum.DEKLARATION]: 'Inkomstdeklarationen till Skatteverket',
    [PhaseEnum.EFTER_STAMMAN]: 'Stämmans beslut bokförs i det nya året',
};

/** Banner shown when the user arrives at the first step of a new phase —
 *  the "you just crossed a boundary" moment the single wizard hid. */
export const PHASE_INTROS = {
    [PhaseEnum.ARSREDOVISNING]:
        'Bokslutet är klart och året är låst — siffrorna kan inte längre ändras. '
        + 'Nu börjar årsredovisningen: det offentliga dokument som byggs från det låsta '
        + 'året, skrivs under av styrelsen, fastställs av stämman och lämnas till Bolagsverket.',
    [PhaseEnum.DEKLARATION]:
        'Nu återstår deklarationen. Uppgifterna hämtas ur det låsta bokslutet — '
        + 'inget räknas om — och lämnas till Skatteverket.',
    [PhaseEnum.EFTER_STAMMAN]:
        'Sista fasen: stämmans beslut om resultatet bokförs — i det nya räkenskapsåret — '
        + 'och bokslutet avslutas.',
};

export function stepPhase(step) {
    return STEP_PHASES[step] || PhaseEnum.BOKSLUT;
}

export const STEP_TITLES = {
    [StepEnum.PREFLIGHT]: 'Förberedelser',
    [StepEnum.RECONCILIATION]: 'Stäm av kontona',
    [StepEnum.ADJUSTMENTS]: 'Bokslutshändelser',
    [StepEnum.TAX]: 'Skatt & dispositioner',
    [StepEnum.ANNUAL_REPORT]: 'Årsredovisning',
    [StepEnum.SIGNATURES_STAMMA]: 'Underskrifter & årsstämma',
    [StepEnum.BOLAGSVERKET]: 'Skicka till Bolagsverket',
    [StepEnum.DEKLARATION]: 'Deklarera (INK2)',
    [StepEnum.POST_STAMMA]: 'Efter stämman',
};

/** Short sublabels for the progress tracker. */
export const STEP_DESCRIPTIONS = {
    [StepEnum.PREFLIGHT]: 'Kontrollera att allt är på plats',
    [StepEnum.RECONCILIATION]: 'Gå igenom alla saldon',
    [StepEnum.ADJUSTMENTS]: 'Periodiseringar med mera',
    [StepEnum.TAX]: 'Beräkna årets skatt',
    [StepEnum.ANNUAL_REPORT]: 'Ta fram årsredovisningen',
    [StepEnum.SIGNATURES_STAMMA]: 'Skriv under och håll stämma',
    [StepEnum.BOLAGSVERKET]: 'Lämna in årsredovisningen',
    [StepEnum.DEKLARATION]: 'Lämna inkomstdeklarationen',
    [StepEnum.POST_STAMMA]: 'Bokför stämmans beslut',
};

export const STEP_ICONS = {
    [StepEnum.PREFLIGHT]: ClipboardCheck,
    [StepEnum.RECONCILIATION]: Scale,
    [StepEnum.ADJUSTMENTS]: SlidersHorizontal,
    [StepEnum.TAX]: Calculator,
    [StepEnum.ANNUAL_REPORT]: FileText,
    [StepEnum.SIGNATURES_STAMMA]: PenLine,
    [StepEnum.BOLAGSVERKET]: Send,
    [StepEnum.DEKLARATION]: FileSpreadsheet,
    [StepEnum.POST_STAMMA]: Flag,
};

/**
 * Company-form label overrides. The blankettset differs by form: AB filar
 * INK2, enskild firma NE-bilaga till INK1, handelsbolag INK4 — showing
 * "Deklarera (INK2)" to an enskild näringsidkare is simply wrong.
 */
export const FORM_STEP_TITLES = {
    ef_forenklat: { [StepEnum.DEKLARATION]: 'Deklarera (NE-bilaga)', [StepEnum.TAX]: 'Årets resultat' },
    ef_arsbokslut: { [StepEnum.DEKLARATION]: 'Deklarera (NE-bilaga)', [StepEnum.TAX]: 'Årets resultat' },
    hb_arsbokslut: {
        [StepEnum.DEKLARATION]: 'Deklarera (INK4)',
        [StepEnum.TAX]: 'Årets resultat',
    },
};

export const FORM_STEP_DESCRIPTIONS = {
    ef_forenklat: {
        [StepEnum.DEKLARATION]: 'NE-bilaga till din inkomstdeklaration',
        [StepEnum.TAX]: 'För över resultatet till eget kapital',
    },
    ef_arsbokslut: {
        [StepEnum.DEKLARATION]: 'NE-bilaga till din inkomstdeklaration',
        [StepEnum.TAX]: 'För över resultatet till eget kapital',
    },
    hb_arsbokslut: {
        [StepEnum.DEKLARATION]: 'Lämna bolagets inkomstdeklaration',
        [StepEnum.TAX]: 'Fördela resultatet på delägarna',
    },
};

/** Step title for a process type (falls back to the AB-shaped default). */
export function stepTitle(step, processType) {
    return FORM_STEP_TITLES[processType]?.[step] || STEP_TITLES[step] || step;
}

/** Step sublabel for a process type. */
export function stepDescription(step, processType) {
    return FORM_STEP_DESCRIPTIONS[processType]?.[step] || STEP_DESCRIPTIONS[step] || '';
}

/** Only these steps are functional; the rest render as
 *  "Kommer snart" placeholders. */
export const FUNCTIONAL_STEPS = [
    StepEnum.PREFLIGHT,
    StepEnum.RECONCILIATION,
    StepEnum.ADJUSTMENTS,
    StepEnum.TAX,
    StepEnum.ANNUAL_REPORT,
    StepEnum.SIGNATURES_STAMMA,
    // Manuell inlämningsbekräftelse (Codex-blocker 2) — steget är byggt;
    // att det saknades här låste det som "kommer snart" (Codex-fynd 2026-08-12).
    StepEnum.BOLAGSVERKET,
    StepEnum.DEKLARATION,
    StepEnum.POST_STAMMA,
];

/** Plain-Swedish descriptions of what each (not yet built) step will do —
 *  shown on the locked placeholder cards. */
export const STEP_COMING_SOON_TEXT = {
    [StepEnum.ADJUSTMENTS]:
        'Här kommer du att få hjälp med bokslutshändelser — till exempel periodiseringar '
        + '(att flytta intäkter och kostnader till rätt år), avskrivningar (värdeminskning på '
        + 'inventarier) och semesterlöneskuld. Systemet föreslår bokföringen och du godkänner.',
    [StepEnum.TAX]:
        'Här beräknas årets skatt. Du får också förslag på så kallade bokslutsdispositioner — '
        + 'lagliga sätt att skjuta upp skatt, till exempel periodiseringsfond — med en tydlig '
        + 'förklaring av om det lönar sig för dig. Till sist bokförs årets resultat och året låses.',
    [StepEnum.ANNUAL_REPORT]:
        'Här tas årsredovisningen fram — den officiella sammanfattningen av företagets år som '
        + 'alla aktiebolag måste upprätta. Det mesta byggs automatiskt från din bokföring, och du '
        + 'svarar bara på några frågor om året som gått.',
    [StepEnum.SIGNATURES_STAMMA]:
        'Här samlar du in styrelsens underskrifter och håller årsstämma — det formella möte där '
        + 'ägarna fastställer årsredovisningen. Du får ett färdigt protokoll att utgå ifrån.',
    [StepEnum.BOLAGSVERKET]:
        'Här bekräftar du inlämningen av årsredovisningen till Bolagsverket. Du laddar ner den '
        + 'inlämningsfärdiga kopian med fastställelseintyg, postar den, och registrerar datum och '
        + 'kanal som bevis. Digital inlämning via Bolagsverkets API kommer i en senare version.',
    [StepEnum.DEKLARATION]:
        'Här förbereds företagets inkomstdeklaration (blankett INK2) med siffror från bokslutet. '
        + 'Du får färdiga filer och en steg-för-steg-guide för att lämna in dem till Skatteverket.',
    [StepEnum.POST_STAMMA]:
        'Efter årsstämman bokförs stämmans beslut — till exempel utdelning till ägarna — och '
        + 'bokslutet avslutas. Vi kontrollerar också att nästa räkenskapsår finns på plats.',
};

/**
 * Readiness-check metadata keyed on the backend's check keys.
 * `explanation` = plain-Swedish "why does this matter".
 * `link(companyId)` = deep link to the surface where the user fixes it
 * (shown when the check is warn/fail).
 */
export const READINESS_CHECK_INFO = {
    ledger_integrity: {
        explanation: 'Kontrollerar att ingående balanser summerar till noll, att tidigare års '
            + 'resultat är omförda och att gamla bokslutsbelopp inte ligger kvar på '
            + 'resultatkontona. Fel här gör att balansräkningen inte kan stämma — de måste '
            + 'åtgärdas innan bokslutet (och deklarationen) kan bli korrekta.',
    },

    fiscal_year_ended: {
        explanation:
            'Bokslutet är en summering av ett helt räkenskapsår. Därför går det bara att göra '
            + 'när årets slutdatum har passerat — annars saknas siffror för resten av året.',
        detailText: (d) => (d?.endDate
            ? `Året slutar ${d.endDate} — kom tillbaka när datumet har passerat.`
            : null),
    },
    target_year_open: {
        explanation:
            'Året är redan låst i bokföringen. Bokslutet bokför justeringar, skatt och årets '
            + 'resultat — det går bara i ett öppet år. Öppna året igen från räkenskapsårssidan '
            + 'om du vill göra bokslutet här.',
        link: (companyId) => `/${companyId}/settings/fiscal-years`,
        linkLabel: 'Gå till räkenskapsår',
    },
    previous_years_closed: {
        explanation:
            'Tidigare räkenskapsår måste vara avslutade först. Årets ingående saldon — det du '
            + '"tar med dig" in i året — hämtas från förra årets bokslut, så om det inte är klart '
            + 'kan årets siffror bli fel.',
        link: (companyId) => `/${companyId}/settings/fiscal-years`,
        linkLabel: 'Gå till räkenskapsår',
    },
    vat_periods_closed: {
        explanation:
            'Alla momsperioder som hör till året behöver vara stängda. Så länge en period är '
            + 'öppen kan momssiffrorna fortfarande ändras — och då stämmer inte bokslutet.',
        naExplanation:
            'Företaget är inte momsregistrerat, så det finns inga momsperioder att stänga.',
        link: (companyId) => `/${companyId}/vat-periods`,
        linkLabel: 'Gå till momsperioder',
    },
    verifications_balanced: {
        explanation:
            'Varje verifikat (en bokförd händelse) måste balansera — lika mycket på debetsidan '
            + 'som på kreditsidan. Ett obalanserat verifikat gör att rapporterna inte går ihop.',
        link: (companyId) => `/${companyId}/verifications`,
        linkLabel: 'Visa verifikat',
    },
    bank_lines_matched: {
        explanation:
            'Alla transaktioner på bankkontot behöver vara matchade mot bokföringen. Om något '
            + 'på kontoutdraget inte är bokfört saknas den händelsen i bokslutet.',
        detailText: (d) => (d?.unmatchedCount
            ? `${d.unmatchedCount} banktransaktion${d.unmatchedCount === 1 ? '' : 'er'} är inte avstämd${d.unmatchedCount === 1 ? '' : 'a'}.`
            : null),
        link: (companyId) => `/${companyId}/bankstatements`,
        linkLabel: 'Gå till bankhändelser',
    },
    periodisering_current: {
        explanation: 'Periodiseringsplanerna ska vara bokförda fram till årets slut, även när automatisk bokföring är avstängd.',
        detailText: (d) => d?.owedAmount > 0
            ? `${formatSek(d.owedAmount)} återstår i ${d.planCount} planer till och med ${d.requiredThrough}.`
            : null,
        link: (companyId) => `/${companyId}/periodisering`,
        linkLabel: 'Gå till periodiseringar',
    },
    depreciation_current: {
        explanation:
            'Avskrivningar — den bokförda värdeminskningen på till exempel maskiner och '
            + 'inventarier — ska vara bokförda fram till årets slut. Annars blir årets kostnader '
            + 'för låga och resultatet för högt.',
        naExplanation:
            'Du har inga tillgångar i anläggningsregistret, så det finns inget att skriva av. '
            + 'Registrerar du tillgångar under Tillgångar aktiveras den här kontrollen.',
        detailText: (d) => (d?.requiredThrough
            ? `Avskrivningar är bokförda t.o.m. ${d.latestPostedPeriod || '— (inga alls)'} men behöver vara bokförda t.o.m. ${d.requiredThrough}.`
            : null),
        link: (companyId) => `/${companyId}/assets`,
        linkLabel: 'Gå till tillgångar',
    },
    supplier_invoices_handled: {
        explanation:
            'Leverantörsfakturor som fortfarande väntar på hantering är inte bokförda — '
            + 'kostnaden saknas då i bokslutet. Bokför eller avvisa dem innan året stängs.',
        detailText: (d) => (d?.unhandledCount
            ? `${d.unhandledCount} leverantörsfaktur${d.unhandledCount === 1 ? 'a' : 'or'} med fakturadatum i året väntar på hantering.`
            : null),
        link: (companyId) => `/${companyId}/supplier-invoices`,
        linkLabel: 'Gå till leverantörsfakturor',
    },
    customer_invoices_handled: {
        explanation:
            'Kundfakturor som fortfarande är utkast är inte bokförda — '
            + 'intäkten saknas då i bokslutet. Slutför eller ta bort utkasten innan året stängs.',
        detailText: (d) => (d?.draftCount
            ? `${d.draftCount} kundfaktur${d.draftCount === 1 ? 'a' : 'or'} med fakturadatum i året är kvar som utkast.`
            : null),
        link: (companyId) => `/${companyId}/invoices`,
        linkLabel: 'Gå till kundfakturor',
    },
    inbox_items_handled: {
        explanation:
            'Poster i inkorgen som inte hanterats kan vara kvitton eller fakturor som hör '
            + 'till året — så länge de ligger kvar är de inte bokförda.',
        detailText: (d) => (d?.unhandledCount
            ? `${d.unhandledCount} post${d.unhandledCount === 1 ? '' : 'er'} mottag${d.unhandledCount === 1 ? 'en' : 'na'} under året väntar i inkorgen.`
            : null),
        link: (companyId) => `/${companyId}/inbox`,
        linkLabel: 'Gå till inkorgen',
    },
    expenses_handled: {
        explanation:
            'Utlägg som inte är bokförda saknas som kostnader i bokslutet. '
            + 'Godkänn och bokför dem — eller avvisa dem som inte ska med.',
        detailText: (d) => (d?.unhandledCount
            ? `${d.unhandledCount} utlägg med datum i året är inte bokför${d.unhandledCount === 1 ? 't' : 'da'}.`
            : null),
        link: (companyId) => `/${companyId}/expenses`,
        linkLabel: 'Gå till utlägg',
    },
    payroll_booked: {
        explanation:
            'Alla lönekörningar med utbetalningsdatum i året behöver vara bokförda — '
            + 'annars saknas lönekostnaderna i bokslutet.',
        naExplanation:
            'Inga lönekörningar finns registrerade för året, så det finns inget att bokföra här.',
        detailText: (d) => (d?.unbookedCount
            ? `${d.unbookedCount} lönekörning${d.unbookedCount === 1 ? '' : 'ar'} saknar bokfört verifikat.`
            : null),
        link: (companyId) => `/${companyId}/payroll/periods`,
        linkLabel: 'Gå till lönekörningar',
    },
    cash_method_unpaid_booked: {
        explanation:
            'Med kontantmetoden bokförs fakturor först när de betalas — men vid årets slut '
            + 'ska alla då obetalda kundfordringar och leverantörsskulder bokföras '
            + '(bokföringslagen 5 kap. 2 §). Annars saknas både intäkter, kostnader och '
            + 'moms i bokslutet. Har du redan bokfört dem manuellt kan du gå vidare.',
        naExplanation:
            'Företaget använder faktureringsmetoden — fakturor bokförs redan när de '
            + 'skickas eller tas emot, så det finns inget att komplettera vid årets slut.',
        detailText: (d) => {
            const parts = [];
            if (d?.customerCount) parts.push(`${d.customerCount} obetald${d.customerCount === 1 ? '' : 'a'} kundfaktur${d.customerCount === 1 ? 'a' : 'or'}`);
            if (d?.supplierCount) parts.push(`${d.supplierCount} obetald${d.supplierCount === 1 ? '' : 'a'} leverantörsfaktur${d.supplierCount === 1 ? 'a' : 'or'}`);
            return parts.length
                ? `${parts.join(' och ')} med datum i året — de ska bokföras som fordran/skuld per balansdagen. Listan finns i steget Bokslutshändelser.`
                : null;
        },
    },
    obs_konto_zero: {
        explanation:
            'OBS-kontot (2999) är en tillfällig parkeringsplats för poster man inte visste '
            + 'hur de skulle bokföras. Innan bokslutet ska saldot utredas och flyttas till '
            + 'rätt konto — det får inte följa med in i årsredovisningen.',
        detailText: (d) => (d?.balance
            ? `Saldot på 2999 är ${formatSek(d.balance)} — utred posterna och boka om dem till rätt konto.`
            : null),
        link: (companyId) => `/${companyId}/verifications`,
        linkLabel: 'Visa verifikat',
    },
};

const sekFormatter = new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

/** Format a (possibly string) amount as SEK with sv-SE locale. */
export function formatSek(value) {
    const n = typeof value === 'string' ? parseFloat(value) : value;
    return sekFormatter.format(Number.isFinite(n) ? n : 0);
}

/**
 * Procenttal i svensk form: 7.96 → "7,96".
 *
 * Bodde tidigare bara i TaxPlanningPage, dit den skrevs när EF-kortet visade
 * "7.96" bredvid "125,94". Year-end-stegen hade ingen motsvarighet och
 * återupprepade felet — NE-steget skrev ut räntesatsen rakt av. Samma
 * rättelse på två ställen som inte känner till varandra driver isär; därför
 * ligger den här och importeras.
 */
export function fmtPct(value) {
    if (value == null || value === '') return '';
    return String(value).replace('.', ',');
}
